import React, { useRef, useState } from "react";
import { Upload, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export type Uploaded = { id: string; url: string; media: string; content_type: string; filename: string; size: number };

const ACCEPT: Record<string, string> = {
  image: "image/jpeg,image/png,image/webp,image/gif", video: "video/mp4,video/webm,video/quicktime",
  audio: "audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/webm,audio/ogg", pdf: "application/pdf",
  any: "image/*,video/mp4,video/webm,video/quicktime,audio/*,application/pdf",
};

export const absUrl = (u?: string | null) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u || "");
export const authedUrl = (u?: string | null) => {
  const a = absUrl(u);
  const t = localStorage.getItem("tt_token");
  return a && a.includes("/api/files/") && t ? `${a}?auth=${encodeURIComponent(t)}` : a;
};
export const mediaKind = (u: string) => {
  const ext = (u.split("?")[0].split(".").pop() || "").toLowerCase();
  if (["mp4", "webm", "mov"].includes(ext)) return "video";
  if (["mp3", "m4a", "wav", "ogg", "aac"].includes(ext)) return "audio";
  if (ext === "pdf") return "pdf";
  return "image";
};

export function Media({ url, className = "", authed = false }: { url: string; className?: string; authed?: boolean }) {
  const src = authed ? authedUrl(url) : absUrl(url);
  const kind = mediaKind(url);
  if (kind === "video") return <video src={src} controls preload="metadata" className={`w-full rounded-md bg-[#121110] ${className}`} data-testid="media-video" />;
  if (kind === "audio") return <audio src={src} controls preload="metadata" className={`w-full ${className}`} data-testid="media-audio" />;
  if (kind === "pdf") return <a href={src} target="_blank" rel="noreferrer" className={`flex items-center gap-2 card-tactile p-3 text-sm hover:underline ${className}`} data-testid="media-pdf"><FileText size={16} /> Open PDF</a>;
  return <img src={src} alt="" className={`rounded-md object-cover w-full bg-[#1A1917] ${className}`} data-testid="media-image" />;
}

export function Uploader({ accept = "any", visibility = "public", multiple = false, onDone, label, testId = "uploader" }: {
  accept?: keyof typeof ACCEPT; visibility?: "public" | "private"; multiple?: boolean; onDone: (files: Uploaded[]) => void; label?: string; testId?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const out: Uploaded[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append("file", files[i]);
        fd.append("visibility", visibility);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (p) => setProgress(Math.round(((i + (p.loaded / (p.total || p.loaded))) / files.length) * 100)),
        });
        out.push(data);
      }
      onDone(out);
      toast.success(`${out.length} file${out.length > 1 ? "s" : ""} uploaded`);
    } catch (er) { toast.error(errMsg(er)); } finally { setProgress(null); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div>
      <input ref={ref} type="file" accept={ACCEPT[accept]} multiple={multiple} className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={progress !== null} className="btn-ghost !py-2 text-xs w-full border-dashed" data-testid={`${testId}-btn`}>
        <Upload size={14} /> {progress !== null ? `Uploading ${progress}%` : label || "Upload file"}
      </button>
      {progress !== null && <div className="h-1 bg-[#D8D4C9] rounded mt-1.5 overflow-hidden"><div className="h-full bg-[#581C25] transition-[width]" style={{ width: `${progress}%` }} /></div>}
    </div>
  );
}

export function FileChips({ files, onRemove }: { files: Uploaded[]; onRemove: (i: number) => void }) {
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {files.map((f, i) => <span key={f.id} className="chip !cursor-default gap-1.5" data-testid={`file-chip-${f.id}`}>{f.media} · {f.filename.slice(0, 22)}<button type="button" onClick={() => onRemove(i)}><X size={11} /></button></span>)}
    </div>
  );
}
